import { useEffect, useMemo, useState } from 'react';
// @ts-ignore
import { useCollection } from 'react-firebase-hooks/firestore';
import {
  query,
  collection,
  where,
  or,
  DocumentData,
  Query,
} from 'firebase/firestore';
import {
  collectSunrunParticipantIds,
  getChatUserRoleFromSplatUserType,
  getChatroomCustomerContactIds,
  getUnreadCountForParticipant,
} from '../lib/chat-unread.utils';
import { DBChatroomData } from '../lib/db-interfaces';
import { firebaseDb } from '../lib/firebase';
import { useFirebaseUser } from './Auth';
import { useUserProfile } from './UserProfile';

const buildChatroomsParticipantQuery = (
  firebaseUserId: string,
  sunrunContactIds: string[],
): Query<DocumentData> => {
  const chatrooms = collection(firebaseDb, 'chatrooms');
  const uidWhere = where('firebaseUserIds', 'array-contains', firebaseUserId);

  if (sunrunContactIds.length === 0) {
    return query(chatrooms, uidWhere);
  }

  return query(
    chatrooms,
    or(
      uidWhere,
      where('sunrunContactIds', 'array-contains', sunrunContactIds[0]),
      ...sunrunContactIds
        .slice(1)
        .map((contactId) =>
          where('sunrunContactIds', 'array-contains', contactId),
        ),
    ),
  );
};

const useUnreadChats = () => {
  const { firebaseUser, loadingFirebaseUser, errorFirebaseUser } =
    useFirebaseUser();
  const { splatUserType, userProfile } = useUserProfile();
  const [jwtSunrunContactId, setJwtSunrunContactId] = useState<string>();

  const chatUserRole = getChatUserRoleFromSplatUserType(splatUserType);
  const participantContactIds = useMemo(
    () =>
      collectSunrunParticipantIds(jwtSunrunContactId, userProfile?.contactId),
    [jwtSunrunContactId, userProfile?.contactId],
  );

  useEffect(() => {
    if (!firebaseUser) {
      setJwtSunrunContactId(undefined);
      return;
    }

    firebaseUser.getIdTokenResult().then((idTokenResult) => {
      const claim = idTokenResult.claims.sunrunContactId;
      setJwtSunrunContactId(typeof claim === 'string' ? claim : undefined);
    });
  }, [firebaseUser]);

  const chatroomsQuery = useMemo(() => {
    if (loadingFirebaseUser || errorFirebaseUser || !firebaseUser?.uid) {
      return null;
    }

    return buildChatroomsParticipantQuery(
      firebaseUser.uid,
      participantContactIds,
    );
  }, [
    loadingFirebaseUser,
    errorFirebaseUser,
    firebaseUser?.uid,
    participantContactIds,
  ]);

  const [value, loading, error] = useCollection(chatroomsQuery, {});

  const unReadChatsByContactId = useMemo(() => {
    if (!participantContactIds.length || !chatUserRole) {
      return {} as Record<string, number>;
    }

    const acc: Record<string, number> = {};
    const seenDocumentIds = new Set<string>();

    for (const doc of value?.docs ?? []) {
      if (seenDocumentIds.has(doc.id)) {
        continue;
      }
      seenDocumentIds.add(doc.id);

      const chatroomData = doc.data() as DBChatroomData;
      const unreadCount = getUnreadCountForParticipant({
        chatroomData,
        contactIds: participantContactIds,
        role: chatUserRole,
      });

      if (unreadCount <= 0) {
        continue;
      }

      for (const customerContactId of getChatroomCustomerContactIds(
        doc.id,
        chatroomData,
      )) {
        acc[customerContactId] = unreadCount;
      }
    }

    return acc;
  }, [value, participantContactIds, chatUserRole]);

  return { unReadChatsByContactId, loading, error };
};

export { useUnreadChats };
